import type * as React from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { ToolPanel } from "../../tool-panel";
import { ToolPanelActions } from "../../tool-panel-actions";
import type { DownloadFormat } from "./components/download-menu";
import { QueryErrorMessages } from "./components/query-error-messages";
import { QueryFilters } from "./components/query-filters";
import { QueryLoadingScreen } from "./components/query-loading-screen";
import { QueryResults } from "./components/query-results";
import { QuerySelector } from "./components/query-selector";
import { SpatialSelection } from "./components/spatial-selection";
import {
	useQueryState,
	type UseQueryStateProps,
} from "./hooks/use-query-state";
import { QueryResults as QueryResultsType, QueryErrorState } from "./types";

export interface QueryToolProps extends UseQueryStateProps {
	/** Panel title */
	title?: string;
	/** Callback when close button is clicked */
	onClose?: () => void;
	/** Callback when primary action (Submit) is clicked */
	onPrimaryAction?: () => void;
	/** Callback when secondary action (Clear) is clicked */
	onSecondaryAction?: () => void;
	/** Callback when download is requested */
	onDownload?: (format: DownloadFormat) => void;

	results?: QueryResultsType;
	queryError?: QueryErrorState;
	page?: number;
	totalPages?: number;
	onPageChange?: (page: number) => void;
	/** Callback when a row is clicked for feature highlighting */
	onRowClick?: (row: any) => void;
	/** Array of column names to exclude from results table */
	excludeColumns?: string[];
}

export function QueryTool({
	title = "Query",
	queries,
	lastUsedQuery,
	simulateError = false,
	simulateResults,
	onClose,
	onPrimaryAction,
	onSecondaryAction,
	onDownload,
	onQuerySelect,
	onFilterChange,
	onSpatialSelectionChange,
	onClear,
	onSubmit,
	results,
	queryError,
	page,
	totalPages,
	onPageChange,
	onRowClick,
	excludeColumns = [],
}: QueryToolProps): React.JSX.Element {
	const queryState = useQueryState({
		queries,
		lastUsedQuery,
		simulateError,
		simulateResults,
		onQuerySelect,
		onFilterChange,
		onSpatialSelectionChange,
		onClear,
		onSubmit,
	});

	const {
		selectedQuery,
		filterValues,
		spatialSelection,
		loading,
		error,
		// results: mockResults,
		// queryError: mockQueryError,
		showResults,
		handleQueryChange,
		handleFilterChange,
		handleSpatialSelectionChange,
		handleClear,
		handleSubmit,
		handleRetry,
		handleNewQuery,
		canSubmit,
		hasFilters,
		hasSpatialSelection,
	} = queryState;

	const handlePrimaryClick = async () => {
		await handleSubmit();
		onPrimaryAction?.();
	};

	const handleSecondaryClick = () => {
		handleClear();
		onSecondaryAction?.();
	};

	const handleDownload = (format: DownloadFormat) => {
		onDownload?.(format);
	};

	const handleNewQueryClick = () => {
		handleNewQuery();
		onSecondaryAction?.();
	};

	// Show results view if we have successful results
	if (showResults && results) {
		return (
			<QueryResults
				results={results}
				queryFields={selectedQuery?.schema?.fields}
				title={title}
				onClose={onClose}
				onNewQuery={handleNewQueryClick}
				onDownload={handleDownload}
				allowedDownloadFormats={selectedQuery?.schema?.allowedDownloadFormats}
				page={page}
				totalPages={totalPages}
				onPageChange={onPageChange}
				excludeColumns={excludeColumns}
				onRowClick={onRowClick}
			/>
		);
	}

	// Show query form (default view)
	return (
		<ToolPanel
			title={title}
			onClose={onClose}
			actions={
				<ToolPanelActions
					variant="clear-submit"
					primaryAction={{
						label: "Submit",
						onClick: handlePrimaryClick,
						disabled: !canSubmit,
						loading: loading,
					}}
					secondaryAction={{
						label: "Clear",
						onClick: handleSecondaryClick,
						disabled: !(queryState as any).canClear,
					}}
				/>
			}>
			<Stack spacing={1}>
				{/* Description above query selector */}
				<Typography
					variant="caption"
					color="text.secondary">
					Choose a layer to search and filter its features using the tools
					below.
				</Typography>

				{/* Query Selection */}
				<QuerySelector
					queries={queries}
					value={selectedQuery}
					onChange={handleQueryChange}
					loading={loading}
					error={error ?? undefined}
				/>

				{/* Selected query description */}
				{selectedQuery?.description && (
					<Typography
						variant="caption"
						color="text.secondary">
						{selectedQuery.description}
					</Typography>
				)}

				{selectedQuery && <Divider />}

				{/* Query Configuration */}
				{selectedQuery && (
					<Stack spacing={1}>
						{/* Loading State */}
						{loading && (
							<QueryLoadingScreen
								message={
									results
										? "Executing query..."
										: "Loading query configuration..."
								}
								showProgress={true}
							/>
						)}

						{/* Error State */}
						{error && (
							<Stack spacing={1}>
								<Alert severity="error">{error}</Alert>
								<Button
									variant="outlined"
									onClick={handleRetry}
									size="small">
									Retry
								</Button>
							</Stack>
						)}

						{/* Query Error Messages (no results, too many results, etc.) */}
						{queryError && (
							<QueryErrorMessages
								errorState={queryError}
								queryFields={selectedQuery.schema?.fields}
								onRetry={handleRetry}
								disabled={loading}
							/>
						)}

						{/* Filters and Spatial Selection */}
						{!loading && !error && selectedQuery.schema && (
							<Stack spacing={3}>
								{/* Filters Section */}
								{hasFilters && (
									<QueryFilters
										schema={selectedQuery.schema}
										values={filterValues}
										onChange={handleFilterChange}
										disabled={loading}
									/>
								)}

								{/* Spatial Selection Section */}
								{hasSpatialSelection && (
									<SpatialSelection
										schema={selectedQuery.schema}
										value={spatialSelection}
										onChange={handleSpatialSelectionChange}
										disabled={loading}
									/>
								)}
							</Stack>
						)}
					</Stack>
				)}
			</Stack>
		</ToolPanel>
	);
}
